import { useEffect, useRef } from "react";
import Label from "components/atoms/forms/Label";
import { ImageUploadIcon } from "components/atoms/Icon";
import BigImageUpload from "components/atoms/UploadImage";
import { useFormContext } from "react-hook-form";
import { handleAllowWholeNumberAsInput } from "utils/HelperFunctions";
import ErrorMessage from "components/atoms/forms/ErrorMessage";
import { Link } from "react-router-dom";
import { useRouter } from "components/hooks";
import { ArrowUpTrayIcon, PencilIcon, XMarkIcon } from "@heroicons/react/24/solid";
import clsx from "clsx";
import InputFieldWithLabel from "../../molecules/forms/InputFieldWithLabel";
import TextAreaWithLabel from "../../molecules/forms/TextAreaWithLabel";

function MagnetCreateForm({ isMagnetPublished }: { isMagnetPublished?: boolean }) {
  const {
    register,
    formState: { errors },
    watch,
    getValues,
    setValue,
  } = useFormContext<CreateUpdateMagnetFormType>();
  const { query } = useRouter();

  const fileInputRef = useRef<HTMLInputElement>(null);

  const magnetImage = watch("image") && watch("image")[0];

  const handleSetImageValue = (file: FileList) => {
    setValue("image", file, {
      shouldValidate: true,
    });
  };
  const handleClearImage = () => {
    setValue("image", null);
  };

  const handleSetCSV = (file: File) => {
    setValue("members_csv", file, {
      shouldValidate: true,
    });
  };
  const handleClearCSV = () => {
    if (fileInputRef.current) {
      fileInputRef.current.files = new DataTransfer().files;
    }
    setValue("members_csv", null);
  };

  useEffect(() => {
    if (errors.image) {
      handleClearImage();
    }
  }, [errors.image]);

  return (
    <div className="grid grid-cols-12 gap-4 2xl:gap-5">
      <p className="col-span-12 font-satoshi  text-xs font-semibold text-secondary">
        Disclaimer: Once the Magnet is published, this data can&apos;t be edited.
      </p>
      <div className="col-span-4 aspect-1">
        <BigImageUpload
          isDisabled={isMagnetPublished}
          setFieldValue={handleSetImageValue}
          existingImageURL={getValues("image_url")}
          choosenFile={magnetImage}
          label="Drag or Drop or Browse from Local Files"
          // @ts-ignore
          errors={errors.image?.message}
          register={register}
          labelClasses="italic text-xs text-white"
          id="image"
          handleClearImage={handleClearImage}
          icon={<ImageUploadIcon className="h-20 w-20 text-white 2xl:h-24 2xl:w-24" />}
          setGeneratedHash={(hash) => setValue("image_hash", hash)}
        />
        <div className="pb-1 pt-5 text-center text-sm leading-6">
          <p className="font-sora font-medium text-white">Upload your Image</p>
          <p className="font-satoshi font-normal text-white">(PNG, JPG, and GIFs are allowed)</p>
        </div>
      </div>
      <div className="col-span-8 mb-5 flex flex-col gap-0 ">
        <div className="flex w-full gap-8">
          <div className="w-1/2">
            <InputFieldWithLabel
              isDisabled={isMagnetPublished}
              label="Magnet Name *"
              id="name"
              register={register}
              placeHolder="Enter the name of the Magnet."
              defaultValue=""
              errorMessage={errors.name?.message as any}
            />
          </div>
          <div className="w-1/2">
            <InputFieldWithLabel
              isDisabled={isMagnetPublished}
              id="max_supply"
              label="Total Magnet Supply *"
              type="number"
              placeHolder="eg. 254"
              register={register}
              defaultValue=""
              errorMessage={errors.max_supply?.message}
              handleOnKeyDown={handleAllowWholeNumberAsInput}
            />
          </div>
        </div>
        <div className="flex w-full gap-8">
          <div className="w-1/2">
            <TextAreaWithLabel
              isDisabled={isMagnetPublished}
              label="Magnet Description *"
              id="description"
              rows={5}
              placeHolder="Enter the description of the Magnet."
              register={register}
              defaultValue=""
              errorMessage={errors.description?.message}
            />
          </div>
          {getValues("type") === 1 && (
            <div className="w-1/2">
              <TextAreaWithLabel
                isDisabled={isMagnetPublished}
                label="Conditions for Membership *"
                id="membership_conditions"
                rows={5}
                placeHolder="Enter the condtions for users to know how to qualify for membership of this Magnet."
                register={register}
                defaultValue=""
                errorMessage={errors.membership_conditions?.message}
              />
            </div>
          )}
        </div>
        {getValues("type") === 1 && (
          <div className="w-1/2 pr-4 text-white">
            <Label htmlFor="members-csv" label="Add Members' List *" />
            <label
              htmlFor="members-csv"
              className={clsx(
                "-mb-3 flex w-full cursor-pointer justify-between rounded-ll border-1 border-white bg-primary px-3 py-3.5 font-satoshi text-white",
                isMagnetPublished === undefined ? "" : "bg-white bg-opacity-50",
              )}
            >
              {watch("members_csv_url") ? (
                <span>Uploaded file: {watch("members_csv_url")}</span>
              ) : (
                <span className="line-clamp-1 font-medium opacity-70">
                  {watch("members_csv") === undefined || watch("members_csv") === null
                    ? "Click to select CSV having emails of eligible users"
                    : `Selected file: ${watch("members_csv")?.name}`}
                </span>
              )}

              <input
                disabled={isMagnetPublished !== undefined}
                id="members-csv"
                accept=".csv, .CSV"
                onChange={(e) => {
                  if (e.target.files?.length) handleSetCSV(e.target.files[0]);
                }}
                autoComplete="off"
                type="file"
                className="sr-only"
                ref={fileInputRef}
              />
            </label>
            <div
              className={clsx(
                "relative -top-6 left-[calc(100%-2rem)] cursor-pointer",
                !watch("members_csv") ? "pointer-events-none" : "pointer-events-auto",
              )}
            >
              {/* eslint-disable-next-line no-nested-ternary */}
              {watch("members_csv_url") ? (
                <PencilIcon className="pointer-events-none h-5" />
              ) : !watch("members_csv") ? (
                <ArrowUpTrayIcon className="pointer-events-none h-5" />
              ) : (
                <XMarkIcon className="h-5" onClick={handleClearCSV} />
              )}
            </div>
            {isMagnetPublished === undefined ? (
              <div className="flex items-start justify-between gap-4">
                <ErrorMessage errorMessage={errors.members_csv?.message} />
                <a
                  href="https://magnetiq-static.s3.amazonaws.com/media/public/efb9869823394973bf074ded7c09c93f.csv"
                  target="_blank"
                  rel="noreferrer"
                  className="shrink-0 font-satoshi text-cyan-500 underline hover:text-cyan-300"
                >
                  Sample CSV Template
                </a>
              </div>
            ) : (
              <p className="font-satoshi text-accent text-opacity-90">
                To edit your Members&apos; List, please go to the{" "}
                <Link
                  to={`/brand-dashboard?m=${query.magnetId}&tab=EXCLUSIVE+MEMBERS`}
                  className="text-blue-400 underline transition hover:text-blue-500 active:scale-95 active:text-blue-600"
                >
                  &lsquo;Exclusive Members&rsquo;
                </Link>{" "}
                tab
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
export default MagnetCreateForm;
